import { createTheme } from '@mui/material/styles';

// Nikao color scheme based on the charts
const nikaoColors = {
  primary: '#0077B6', // Brand blue: rgb(0, 119, 182)
  secondary: '#00BF8F', // Brand green: rgb(0, 191, 143)
  capacity: '#160E2B', // Black for capacity bars
  forecast: '#0077B6', // Blue for forecast bars
  actual: '#00BF8F', // Green for actual bars
  background: '#ffffff',
  surface: '#f8fafc',
  text: '#1f2937',
  textSecondary: '#6b7280',
  border: '#e5e7eb',
  error: '#dc2626',
  warning: '#d97706',
  success: '#00BF8F',
  info: '#0077B6'
};

export const nikaoTheme = createTheme({
  palette: {
    primary: {
      main: nikaoColors.primary,
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: nikaoColors.secondary,
      light: '#10b981',
      dark: '#047857',
      contrastText: '#ffffff',
    },
    background: {
      default: nikaoColors.background,
      paper: nikaoColors.surface,
    },
    text: {
      primary: nikaoColors.text,
      secondary: nikaoColors.textSecondary,
    },
    error: {
      main: nikaoColors.error,
    },
    warning: {
      main: nikaoColors.warning,
    },
    success: {
      main: nikaoColors.success,
    },
    info: {
      main: nikaoColors.info,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: nikaoColors.text,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: nikaoColors.text,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: nikaoColors.text,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: nikaoColors.text,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: nikaoColors.text,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      color: nikaoColors.text,
    },
    body1: {
      fontSize: '1rem',
      color: nikaoColors.text,
    },
    body2: {
      fontSize: '0.875rem',
      color: nikaoColors.textSecondary,
    },
  },
  shape: {
    borderRadius: 0,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 0,
          padding: '8px 16px',
        },
        contained: {
          backgroundColor: nikaoColors.primary,
          color: '#ffffff',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#005c8f', // Darker shade of primary
            boxShadow: '0 4px 8px rgba(0, 119, 182, 0.3)',
          },
          '&:active': {
            backgroundColor: '#004a73', // Even darker for active state
          },
        },
        outlined: {
          borderColor: nikaoColors.primary,
          color: nikaoColors.primary,
          '&:hover': {
            borderColor: '#005c8f',
            backgroundColor: `${nikaoColors.primary}10`, // 10% opacity
          },
        },
        text: {
          color: nikaoColors.primary,
          '&:hover': {
            backgroundColor: `${nikaoColors.primary}10`, // 10% opacity
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${nikaoColors.border}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: nikaoColors.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: nikaoColors.primary,
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: nikaoColors.primary,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: nikaoColors.primary,
          color: '#ffffff',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#f5f5f5',
          borderRight: `1px solid ${nikaoColors.border}`,
          boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          margin: '4px 8px',
          borderRadius: 0,
          '&:hover': {
            backgroundColor: `${nikaoColors.primary}15`, // Light blue hover
          },
          '&.Mui-selected': {
            backgroundColor: `${nikaoColors.primary}20`, // Slightly more opaque
            color: nikaoColors.primary,
            borderLeft: `3px solid ${nikaoColors.primary}`,
            '&:hover': {
              backgroundColor: `${nikaoColors.primary}25`,
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#666666',
          '.Mui-selected &': {
            color: nikaoColors.primary,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontWeight: 500,
          fontSize: '0.95rem',
          color: '#333333',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '64px',
          padding: '0 24px',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h6: {
          fontWeight: 600,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: nikaoColors.textSecondary,
          '&.Mui-checked': {
            color: nikaoColors.primary,
          },
          '&:hover': {
            backgroundColor: `${nikaoColors.primary}10`,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: nikaoColors.primary,
            '& + .MuiSwitch-track': {
              backgroundColor: nikaoColors.primary,
            },
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: nikaoColors.primary,
          '&:hover': {
            color: '#005c8f',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': {
            color: nikaoColors.primary,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: nikaoColors.primary,
          height: '3px',
        },
      },
    },
  },
  branding: {
    lightLogo: (
        <svg xmlns="http://www.w3.org/2000/svg" width="183" height="28" viewBox="0 0 183 28" fill="none">
        <g id="Layer_1" clipPath="url(#clip0_3735_1551)">
        <path id="Vector" d="M55.6947 27.8507H41.689C39.4772 27.8507 37.3521 27.0025 35.7884 25.4919L32.0128 21.8445C31.6586 21.5023 31.6586 20.9526 32.0128 20.6141L37.0808 15.7181C37.435 15.3759 38.0115 15.3759 38.3619 15.7217L41.4667 18.8559C43.0455 20.3484 44.3304 21.5423 45.3892 22.4997C48.4224 25.2371 49.5867 26.0197 50.3253 26.2381C51.7383 27.2392 53.6223 27.8543 55.6909 27.8543L55.6947 27.8507Z" fill="#00BF8F"/>
        <path id="Vector_2" d="M89.0339 3.31248L66.2677 25.3062C64.5796 26.937 62.2962 27.8471 59.9186 27.8471H55.6984C53.6298 27.8471 51.7458 27.2319 50.3328 26.2308C50.5627 26.2964 50.7511 26.3109 50.9357 26.3109C51.4444 26.3109 51.9418 26.2126 52.4015 26.027C52.8649 25.8413 53.2869 25.572 53.6449 25.2262L61.5614 17.5782L73.7019 5.84966C75.39 4.21887 77.6734 3.30884 80.051 3.30884H89.0339V3.31248Z" fill="#0077B6"/>
        <path id="Vector_3" d="M33.3354 3.31248H47.341C49.5528 3.31248 51.678 4.16063 53.2417 5.67129L56.8853 9.19131C57.2395 9.53349 57.2395 10.0831 56.8853 10.4217L51.8174 15.3177C51.4632 15.6599 50.8867 15.6599 50.5363 15.314L47.5633 12.3073C45.9845 10.8148 44.6996 9.62085 43.6408 8.66349C40.6076 5.9261 39.4433 5.14347 38.7048 4.92506C37.2918 3.92402 35.4078 3.30884 33.3392 3.30884L33.3354 3.31248Z" fill="#00BF8F"/>
        <path id="Vector_4" d="M0 27.8507L22.7662 5.85332C24.4543 4.22254 26.7377 3.3125 29.1153 3.3125H33.3354C35.404 3.3125 37.288 3.92768 38.701 4.92872C38.4712 4.8632 38.2828 4.84864 38.0982 4.84864C37.5895 4.84864 37.0921 4.94693 36.6324 5.13257C36.1689 5.31822 35.7469 5.58759 35.389 5.9334L27.4724 13.5814L15.332 25.3099C13.6439 26.9407 11.3605 27.8507 8.98289 27.8507H0Z" fill="#0077B6"/>
        <path id="Vector_5" d="M99.019 8.64543H103.073V11.6522H103.145C103.846 10.4582 104.746 9.57003 105.854 8.98396C106.962 8.3979 108.156 8.10669 109.438 8.10669C110.719 8.10669 111.845 8.37242 112.881 8.90024C113.918 9.42806 114.75 10.258 115.38 11.3828C116.009 12.5076 116.325 13.9164 116.325 15.6054V27.4249H112.166V17.3599C112.166 16.2133 112.026 15.245 111.747 14.4551C111.468 13.6688 111.02 13.0427 110.402 12.5804C109.784 12.1181 108.985 11.8888 108.006 11.8888C107.026 11.8888 106.239 12.1145 105.508 12.5659C104.773 13.0172 104.196 13.6616 103.778 14.5061C103.36 15.3506 103.149 16.348 103.149 17.4946V27.4249H99.0228V8.64543H99.019Z" fill="#160E2B"/>
        <path id="Vector_6" d="M119.762 4.45916C119.238 3.94226 118.974 3.31251 118.974 2.56628C118.974 2.1149 119.091 1.68901 119.325 1.28131C119.558 0.877253 119.882 0.56056 120.304 0.334871C120.723 0.109182 121.19 -0.00366211 121.702 -0.00366211C122.448 -0.00366211 123.078 0.251148 123.59 0.757128C124.103 1.26311 124.359 1.86737 124.359 2.56264C124.359 3.0577 124.242 3.50908 124.008 3.91313C123.775 4.31719 123.454 4.63752 123.048 4.87413C122.641 5.11074 122.192 5.22723 121.702 5.22723C120.934 5.22723 120.286 4.96878 119.762 4.45188V4.45916ZM119.604 8.64532H123.73V27.4248H119.604V8.64532Z" fill="#160E2B"/>
        <path id="Vector_7" d="M127.068 0.404053H131.228V16.3807H131.299L131.544 16.0749C132.92 14.6115 134.091 13.3666 135.056 12.3437C136.024 11.3209 137.181 10.0868 138.534 8.64534H143.496L136.224 16.2096C138.04 18.663 139.389 20.494 140.263 21.6989C141.138 22.9038 142.449 24.6984 144.197 27.0863L144.476 27.4249H139.615L138.707 26.1399C137.543 24.5419 136.673 23.3552 136.104 22.5762C135.531 21.8008 134.687 20.6323 133.568 19.0816C133.033 19.6204 132.637 20.0208 132.381 20.2792C132.125 20.5377 131.74 20.9381 131.228 21.4769V27.4212H127.068V0.404053Z" fill="#160E2B"/>
        <path id="Vector_8" d="M148.108 27.4067C147.129 27.0136 146.341 26.4166 145.75 25.6157C145.154 24.8186 144.857 23.843 144.857 22.6927C144.857 21.5424 145.143 20.6287 145.712 19.8716C146.281 19.1181 147.046 18.5211 148.003 18.0806C148.96 17.6402 150.075 17.2871 151.36 17.0177C151.571 16.974 151.839 16.9267 152.163 16.883C153.606 16.5918 154.657 16.3589 155.309 16.1914C155.961 16.024 156.447 15.791 156.76 15.4998C157.076 15.2086 157.231 14.8009 157.231 14.284C157.231 13.7671 157.084 13.2829 156.794 12.8316C156.503 12.3802 156.078 12.0271 155.516 11.7686C154.959 11.5102 154.314 11.3791 153.595 11.3791C152.592 11.3791 151.718 11.6594 150.972 12.2237C150.226 12.7879 149.691 13.596 149.363 14.6553L145.482 13.3703C146.04 11.5684 147.072 10.2398 148.576 9.38434C150.079 8.5289 151.82 8.09937 153.802 8.09937C155.294 8.09937 156.617 8.34325 157.77 8.82375C158.923 9.30789 159.827 10.0396 160.479 11.0188C161.131 11.998 161.458 13.2101 161.458 14.648V22.0448C161.481 23.3079 161.541 24.3199 161.632 25.0843C161.726 25.8487 161.877 26.6277 162.088 27.414H157.928C157.788 26.9408 157.687 26.4785 157.63 26.0307C157.57 25.5793 157.543 25.0734 157.543 24.5091H157.472C156.914 25.5684 156.093 26.4129 155.008 27.0427C153.922 27.6724 152.683 27.9891 151.285 27.9891C150.143 27.9891 149.081 27.7926 148.105 27.3994L148.108 27.4067ZM154.94 24.1815C155.675 23.7083 156.243 23.1113 156.654 22.3906C157.061 21.6698 157.265 20.9491 157.265 20.2283V18.4046C156.775 18.6085 156.372 18.7723 156.059 18.896C155.742 19.0198 155.32 19.1508 154.782 19.2855C154.454 19.3547 153.941 19.4675 153.244 19.6241C152.498 19.7806 151.858 19.9662 151.323 20.181C150.787 20.3958 150.332 20.6979 149.958 21.0947C149.585 21.4878 149.401 21.9792 149.401 22.5653C149.401 23.2861 149.676 23.8539 150.222 24.2725C150.769 24.6875 151.5 24.8986 152.408 24.8986C153.365 24.8986 154.209 24.662 154.944 24.1888L154.94 24.1815Z" fill="#160E2B"/>
        <path id="Vector_9" d="M167.898 26.7333C166.383 25.8888 165.208 24.7167 164.367 23.2206C163.527 21.7245 163.109 20.0063 163.109 18.0697C163.109 16.1332 163.527 14.4114 164.367 12.9007C165.208 11.3937 166.383 10.2143 167.898 9.3698C169.413 8.52528 171.138 8.10303 173.071 8.10303C175.004 8.10303 176.726 8.52528 178.23 9.3698C179.733 10.2143 180.905 11.3901 181.741 12.9007C182.582 14.4078 183 16.1332 183 18.0697C183 20.0063 182.582 21.7245 181.741 23.2206C180.901 24.7167 179.733 25.8888 178.23 26.7333C176.726 27.5778 175.008 28.0001 173.071 28.0001C171.135 28.0001 169.413 27.5778 167.898 26.7333ZM175.992 23.4244C176.843 22.8966 177.506 22.1686 177.985 21.2476C178.463 20.3266 178.701 19.2674 178.701 18.0734C178.701 16.8794 178.46 15.8165 177.985 14.881C177.506 13.9455 176.843 13.2138 175.992 12.686C175.14 12.1581 174.168 11.8924 173.071 11.8924C171.975 11.8924 170.995 12.1581 170.136 12.686C169.273 13.2138 168.61 13.9491 168.143 14.881C167.676 15.8165 167.442 16.8794 167.442 18.0734C167.442 19.2674 167.679 20.3266 168.158 21.2476C168.636 22.1722 169.3 22.8966 170.151 23.4244C171.003 23.9522 171.975 24.218 173.071 24.218C174.168 24.218 175.14 23.9522 175.992 23.4244Z" fill="#160E2B"/>
        </g>
        <defs>
        <clipPath id="clip0_3735_1551">
        <rect width="183" height="28" fill="white"/>
        </clipPath>
        </defs>
        </svg>
    ),
    brandName: "Nikao",
  },
});

export { nikaoColors }; 